/**
 * Epic 11 — All-hazards incident type registry.
 *
 * Each type declares its stage profile, ITASC vocabulary hints (prioritised
 * tags), and applicable rule pack. This registry drives:
 *  - the stage stepper (only shows stages for the current type)
 *  - the insight engine (rules scoped by incidentTypes)
 *  - doctrine applicability filtering (incidentTypes dimension)
 *  - ITASC capture (pre-highlighted tags per type)
 */

import type { IncidentStage } from './stages.js';

// ── Incident types ─────────────────────────────────────────────────────────────

export const INCIDENT_TYPES = [
  'StructureFire',
  'GrassFire',
  'MVA',
  'VehicleFire',
  'Hazmat',
  'StormFlood',
  'Alarm',
  'AssistOtherAgency',
  'Other',
] as const;

export type IncidentType = (typeof INCIDENT_TYPES)[number];

// ── Incident levels ────────────────────────────────────────────────────────────

export const INCIDENT_LEVELS = ['Level1', 'Level2', 'Level3', 'IMT'] as const;
export type IncidentLevel = (typeof INCIDENT_LEVELS)[number];

export const INCIDENT_LEVEL_LABELS: Record<IncidentLevel, string> = {
  Level1: 'Level 1',
  Level2: 'Level 2',
  Level3: 'Level 3',
  IMT: 'IMT',
};

// ── Registry ───────────────────────────────────────────────────────────────────

export interface IncidentTypeProfile {
  label: string;
  /** Ordered stage flow for this incident type (subset of INCIDENT_STAGES). */
  stages: readonly IncidentStage[];
  /** Suggested lead agency — advisory only. */
  defaultLeadAgency: string;
  /** ITASC threat tags most relevant (for highlight / pre-select hints). */
  priorityThreatTags: string[];
  /** ITASC action tags most relevant. */
  priorityActionTags: string[];
  /** ITASC support tags most relevant. */
  prioritySupportTags: string[];
  /** Identifier used to scope rules to this type's pack. */
  rulePack: 'fire' | 'rescue' | 'hazmat' | 'alarm' | 'assist' | 'other';
}

export const INCIDENT_TYPE_REGISTRY: Record<IncidentType, IncidentTypeProfile> = {
  StructureFire: {
    label: 'Structure fire',
    stages: [
      'Dispatch', 'EnRoute', 'Arrival', 'Rescue',
      'Suppression', 'Overhaul', 'Investigation', 'StandDown',
    ],
    defaultLeadAgency: 'NSW RFS',
    priorityThreatTags: ['exposures', 'collapse', 'lpg-gas', 'solar-batteries', 'asbestos', 'smoke', 'utilities', 'fire-behaviour-signs'],
    priorityActionTags: ['size-up-dra', 'rescue-search', 'first-attack', 'offensive-internal', 'offensive-external', 'establishing-water', 'ba-entry-control', 'ventilation', 'salvage'],
    prioritySupportTags: ['ba-operators', 'frnsw', 'additional-tanker', 'bulk-water', 'ambulance', 'utilities'],
    rulePack: 'fire',
  },
  GrassFire: {
    label: 'Grass / bush fire',
    stages: ['Dispatch', 'EnRoute', 'Arrival', 'Suppression', 'Overhaul', 'StandDown'],
    defaultLeadAgency: 'NSW RFS',
    priorityThreatTags: [
      'exposures', 'grass-bush-extension', 'ember-attack', 'spot-fires', 'wind-change',
      'access', 'difficult-terrain', 'crown-fire', 'extreme-fire-danger', 'utilities', 'water',
    ],
    priorityActionTags: [
      'size-up-dra', 'first-attack', 'direct-attack', 'indirect-attack',
      'exposure-protection', 'backburning', 'establishing-water',
    ],
    prioritySupportTags: ['additional-tanker', 'bulk-water', 'aircraft', 'raft', 'ambulance', 'police', 'npws-forestry'],
    rulePack: 'fire',
  },
  MVA: {
    label: 'MVA / rescue',
    stages: ['Dispatch', 'EnRoute', 'Arrival', 'Rescue', 'Overhaul', 'StandDown'],
    defaultLeadAgency: 'NSW RFS',
    priorityThreatTags: ['entrapment', 'fuel-spill', 'multiple-vehicles', 'access', 'lpg-gas', 'utilities', 'collapse'],
    priorityActionTags: ['size-up-dra', 'rescue-search', 'extrication', 'traffic-control', 'investigating'],
    prioritySupportTags: ['ambulance', 'police', 'frnsw', 'welfare-rehab', 'lighting'],
    rulePack: 'rescue',
  },
  VehicleFire: {
    label: 'Vehicle fire',
    stages: [
      'Dispatch', 'EnRoute', 'Arrival',
      'Rescue', 'Suppression', 'Overhaul', 'Investigation', 'StandDown',
    ],
    // Full 8-stage flow, matching StructureFire — Rescue is kept (an occupant
    // may still be present even with no third-party collision), unlike MVA's
    // leaner stage list, because this type carries genuine suppression +
    // cause-investigation phases MVA's rulePack doesn't model.
    defaultLeadAgency: 'NSW RFS',
    priorityThreatTags: ['ev-hv-battery', 'thermal-runaway', 'stranded-energy', 'reignition-risk', 'fuel-spill', 'exposures', 'access', 'multiple-vehicles'],
    priorityActionTags: ['size-up-dra', 'first-attack', 'hv-isolation', 'battery-cooling', 'defensive', 'offensive-external', 'battery-monitoring', 'extrication'],
    prioritySupportTags: ['additional-tanker', 'bulk-water', 'frnsw', 'hazmat-team', 'tow-recovery', 'police', 'ambulance'],
    rulePack: 'fire',
  },
  Hazmat: {
    label: 'Hazmat / chemical',
    stages: ['Dispatch', 'EnRoute', 'Arrival', 'Rescue', 'Overhaul', 'Investigation', 'StandDown'],
    defaultLeadAgency: 'FRNSW',
    priorityThreatTags: ['unknown-substance', 'vapour-cloud', 'smoke', 'utilities', 'access', 'exposures'],
    priorityActionTags: ['size-up-dra', 'investigating', 'decontamination', 'rescue-search', 'defensive'],
    prioritySupportTags: ['hazmat-team', 'epa', 'frnsw', 'ambulance', 'police', 'utilities', 'duty-officer'],
    rulePack: 'hazmat',
  },
  StormFlood: {
    label: 'Storm / flood',
    stages: ['Dispatch', 'EnRoute', 'Arrival', 'Rescue', 'Overhaul', 'StandDown'],
    defaultLeadAgency: 'NSW SES',
    priorityThreatTags: ['powerlines-down', 'rising-water', 'access', 'utilities', 'exposures'],
    priorityActionTags: ['size-up-dra', 'flood-rescue', 'rescue-search', 'investigating'],
    prioritySupportTags: ['ses', 'ambulance', 'police', 'utilities', 'duty-officer'],
    rulePack: 'rescue',
  },
  Alarm: {
    label: 'Alarm / automatic',
    stages: ['Dispatch', 'EnRoute', 'Arrival', 'StandDown'],
    defaultLeadAgency: 'FRNSW',
    priorityThreatTags: ['smoke', 'access'],
    priorityActionTags: ['investigating', 'size-up-dra'],
    prioritySupportTags: ['duty-officer'],
    rulePack: 'alarm',
  },
  AssistOtherAgency: {
    label: 'Assist other agency',
    stages: ['Dispatch', 'EnRoute', 'Arrival', 'Overhaul', 'StandDown'],
    defaultLeadAgency: 'NSW RFS',
    priorityThreatTags: ['access', 'exposures'],
    priorityActionTags: ['size-up-dra', 'investigating'],
    prioritySupportTags: ['duty-officer', 'ambulance'],
    rulePack: 'assist',
  },
  Other: {
    label: 'Other',
    stages: [
      'Dispatch', 'EnRoute', 'Arrival', 'Rescue',
      'Suppression', 'Overhaul', 'Investigation', 'StandDown',
    ],
    defaultLeadAgency: 'NSW RFS',
    priorityThreatTags: ['access', 'exposures'],
    priorityActionTags: ['size-up-dra', 'investigating'],
    prioritySupportTags: ['duty-officer', 'ambulance'],
    rulePack: 'other',
  },
};

export const INCIDENT_TYPE_LABELS: Record<IncidentType, string> = Object.fromEntries(
  INCIDENT_TYPES.map((t) => [t, INCIDENT_TYPE_REGISTRY[t].label]),
) as Record<IncidentType, string>;

/** Stage profile for an incident type — ordered stages relevant to this type. */
export function stagesForType(type: IncidentType): readonly IncidentStage[] {
  return INCIDENT_TYPE_REGISTRY[type].stages;
}

/**
 * Reorder a full tag vocabulary so the values in `priority` (the current
 * incident type's `priorityThreatTags`/`priorityActionTags`/`prioritySupportTags`)
 * come first, in the order the registry lists them, followed by everything
 * else in its original order. Never drops a tag — only reorders — so the
 * full vocabulary stays reachable regardless of type; this exists purely so
 * the ITASC chip pickers surface what's actually relevant to this incident
 * first as the tag vocabulary grows across incident types.
 */
export function sortTagsByPriority<T extends string>(all: readonly T[], priority: readonly string[]): T[] {
  const priorityIndex = new Map(priority.map((p, i) => [p, i]));
  return [...all].sort((a, b) => {
    const pa = priorityIndex.get(a);
    const pb = priorityIndex.get(b);
    if (pa !== undefined && pb !== undefined) return pa - pb;
    if (pa !== undefined) return -1;
    if (pb !== undefined) return 1;
    return all.indexOf(a) - all.indexOf(b);
  });
}

/**
 * Coerce a legacy free-text typeReported string to an IncidentType.
 * Used in migrateIncident() to handle incidents created before Epic 11.
 */
export function incidentTypeFromString(s: string): IncidentType {
  // Already a valid IncidentType
  if ((INCIDENT_TYPES as readonly string[]).includes(s)) return s as IncidentType;
  const lower = s.toLowerCase();
  if (/structure|struct/.test(lower)) return 'StructureFire';
  if (/grass|bush|vegetation/.test(lower)) return 'GrassFire';
  // Checked before the broader MVA regex below (which also matches "vehicle")
  // so "vehicle fire" / "car fire" resolve to VehicleFire, not MVA.
  if (/vehicle\s*fire|car\s*fire|truck\s*fire/.test(lower)) return 'VehicleFire';
  if (/mva|motor|vehicle|rta|crash|accident/.test(lower)) return 'MVA';
  if (/hazmat|chemical|spill|gas\s*leak|gas\s*smell|odour|odor/.test(lower)) return 'Hazmat';
  if (/storm|flood/.test(lower)) return 'StormFlood';
  if (/alarm|auto/.test(lower)) return 'Alarm';
  if (/assist/.test(lower)) return 'AssistOtherAgency';
  return 'Other';
}

/**
 * Coerce a legacy level string to an IncidentLevel.
 * Used in migrateIncident() for incidents created before Epic 11.
 */
export function incidentLevelFromString(s: string): IncidentLevel {
  if ((INCIDENT_LEVELS as readonly string[]).includes(s)) return s as IncidentLevel;
  if (/level\s*1/i.test(s)) return 'Level1';
  if (/level\s*2/i.test(s)) return 'Level2';
  if (/level\s*3/i.test(s)) return 'Level3';
  if (/imt/i.test(s)) return 'IMT';
  return 'Level1';
}
